import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import { useGlobalStore } from '../../stores/globalStore';
import { useResidents } from '../../api/residentsApi';
import { useNavPalette } from '../../navigation/useNavSections';
import { useRegisteredShortcuts } from '../../hooks/shortcutRegistry';
import { formatShortcutKey } from '../../hooks/useKeyboardShortcuts';
import type { KeyboardShortcut } from '../../hooks/useKeyboardShortcuts';
import { isSlugDisabled } from '../../pages/reports/disabledReports';
import { usePermission } from '../../hooks/usePermission';
import {
  searchPages,
  searchReports,
  type PaletteItem,
  type ReportSource,
} from './commandPalette';
import type { Patient } from '../../types/patient';
import {
  GLOBAL_SEARCH_DEBOUNCE_MS,
  MAX_ITEMS,
  MAX_RECIPES,
  recipeDisplayName,
  searchableQuery,
  useDebouncedValue,
  useItemSearch,
  useRecipeSearch,
  type ItemSearchHit,
  type RecipeSearchHit,
} from '../../api/globalSearchApi';
import { leaveGuardTakesOver, pageHasUnsavedMarker } from '../../utils/leaveGuard';
import { useConfirm } from '../../hooks/useConfirm';
import ConfirmDialog from './ConfirmDialog';
import './GlobalSearch.css';

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
}

const MAX_RESIDENTS = 8;
const MAX_PAGES = 5;
const MAX_REPORTS = 6;
const MAX_ACTIONS = 5;

/** A single navigable row. Sections are derived from these for rendering. */
type Row =
  | { kind: 'page' | 'report'; item: PaletteItem }
  | { kind: 'resident'; patient: Patient }
  | { kind: 'itemRecord'; hit: ItemSearchHit }
  | { kind: 'recipe'; hit: RecipeSearchHit }
  | { kind: 'action'; shortcut: KeyboardShortcut };

const SECTION_LABELS: Record<Row['kind'], string> = {
  action: 'On this screen',
  page: 'Pages',
  report: 'Reports',
  resident: '', // filled in from the facility's resident label
  itemRecord: 'Items',
  recipe: 'Recipes',
};

/** Where a row goes. Items open Item Add/Mod; recipes open the recipe page. */
function rowPath(row: Exclude<Row, { kind: 'action' }>): string {
  switch (row.kind) {
    case 'resident': return `/people/${row.patient.pKey_Patient}`;
    case 'itemRecord': return `/items/${row.hit.pKey_Item}`;
    case 'recipe': return `/recipes/${row.hit.pKey_RC_Recipe}`;
    default: return row.item.route;
  }
}

function rowKey(row: Row): string {
  switch (row.kind) {
    case 'resident': return `r${row.patient.pKey_Patient}`;
    case 'itemRecord': return `i${row.hit.pKey_Item}`;
    case 'recipe': return `c${row.hit.pKey_RC_Recipe}`;
    case 'action': return `a${row.shortcut.id}`;
    default: return row.item.id;
  }
}

/**
 * Global navigation bindings are excluded from the action list.
 *
 * Ctrl+/ and Ctrl+F both OPEN this palette, so listing them inside it offers
 * her the thing she is already using. Everything else the current screen has
 * bound is exactly what "what can I do here" means.
 */
const NOT_AN_ACTION = new Set(['global-search', 'global-search-find']);

export default function GlobalSearch({ open, onClose }: GlobalSearchProps) {
  const navigate = useNavigate();
  const facilityId = useGlobalStore((s) => s.facilityId);
  const residentPluralLabel = useGlobalStore((s) => s.residentPluralLabel);
  const { data: residents = [] } = useResidents(facilityId);
  // Pages come from the navigation config, already filtered to what this user may open. Before
  // anything is typed the palette lists the sections, as the sidebar rail does.
  const { directory, searchable: searchablePages } = useNavPalette();
  const { itemsLevel, recipeLevel, roleLoaded } = usePermission();
  // Items and recipes are searched only for a role that can open them, the
  // levels their pages need (SecureRoute: 1). The API checks the same.
  const canSearchItems = roleLoaded && itemsLevel >= 1;
  const canSearchRecipes = roleLoaded && recipeLevel >= 1;
  const { confirm, confirmProps } = useConfirm();
  // The registry reports what is bound RIGHT NOW, so a dialog's commands
  // appear while it is open and vanish when it closes. Context-awareness
  // therefore needs no new mechanism — this is the same source F1 reads, which
  // is what keeps the two from disagreeing.
  // Read live. The registry publishes what is bound right now, so a screen's
  // own commands are present while it is open — including behind this dialog,
  // which does not unmount what is behind it.
  const boundShortcuts = useRegisteredShortcuts();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Items and recipes come from the server, so they wait for typing to settle
  // and each is its own query: residents (in the browser) answer at once and
  // never wait on them. Results show only for the settled query, so a list
  // for "chi" never sits under "chicken".
  const settledQuery = useDebouncedValue(query, GLOBAL_SEARCH_DEBOUNCE_MS);
  const serverQuery = open ? searchableQuery(settledQuery) : '';
  const settled = searchableQuery(query) === serverQuery;
  const itemSearch = useItemSearch(facilityId, serverQuery, canSearchItems);
  const recipeSearch = useRecipeSearch(facilityId, serverQuery, canSearchRecipes);
  const itemHits = useMemo(
    () => (settled && serverQuery ? itemSearch.data ?? [] : []),
    [settled, serverQuery, itemSearch.data],
  );
  const recipeHits = useMemo(
    () => (settled && serverQuery ? recipeSearch.data ?? [] : []),
    [settled, serverQuery, recipeSearch.data],
  );
  const searchingRecords = searchableQuery(query) !== ''
    && ((canSearchItems && (!settled || itemSearch.isFetching))
      || (canSearchRecipes && (!settled || recipeSearch.isFetching)));

  // The report catalog is ~34 KB and only matters once someone actually
  // searches, so it is fetched on first use rather than shipped in the initial
  // bundle. (reportConfigs.ts, which holds the authoritative display names, is
  // ~675 KB — hence `titleFromSlug` in commandPalette.ts instead.)
  const [catalog, setCatalog] = useState<Record<string, ReportSource> | null>(null);

  useEffect(() => {
    if (!open || catalog) return;
    let cancelled = false;
    import('../../pages/reports/reportCatalog')
      .then((module) => {
        if (!cancelled) setCatalog(module.REPORT_CATALOG);
      })
      .catch(() => {
        // Reports simply stay out of the results; pages and residents still
        // work. Failing the whole palette over this would be worse.
      });
    return () => { cancelled = true; };
  }, [open, catalog]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      // Focus input after dialog animation
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const matchedResidents = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    const matched: Patient[] = [];
    for (const r of residents) {
      if (r.isDeleted) continue;
      const name = (r.fullName ?? `${r.lastName}, ${r.firstName}`).toLowerCase();
      const room = String(r.room_No ?? '').toLowerCase();
      const bed = (r.bed_No ?? '').toLowerCase();
      const unit = (r.unit ?? '').toLowerCase();
      if (
        name.includes(q) ||
        room.includes(q) ||
        `${room}${bed}`.includes(q) ||
        unit.includes(q)
      ) {
        matched.push(r);
        if (matched.length >= MAX_RESIDENTS) break;
      }
    }
    return matched;
  }, [query, residents]);

  /**
   * One flat list, because that is what arrow keys traverse. Pages first:
   * someone typing "meal" almost always wants to go somewhere, and a
   * destination is a cheaper mistake to recover from than opening a report.
   */
  const rows = useMemo<Row[]>(() => {
    const q = query.trim().toLowerCase();
    // No query: list every destination this user can reach, rather than an
    // empty box. Opening the palette and hearing "9 destinations, 1 Dashboard,
    // 2 Residents..." is a directory; opening it to silence and a blank field
    // requires already knowing what to type, which is the thing a screen
    // reader user is least able to do.
    // Innermost first. The registry lists slots outermost-first, but the
    // dispatcher resolves keys the other way — the innermost mounted scope
    // owns them — and the palette should agree. It matters once a screen
    // nests: the meal pattern tab lives inside Resident Detail, which
    // publishes one command per rail section, so in registry order the two
    // commands belonging to the tab she is actually looking at sat behind
    // thirty belonging to the page around it, and the cap below dropped them.
    const matched: Row[] = [...boundShortcuts].reverse()
      .filter((sc) => !NOT_AN_ACTION.has(sc.id) && sc.enabled !== false)
      .filter((sc) => !q || sc.label.toLowerCase().includes(q))
      .map((sc) => ({ kind: 'action', shortcut: sc }) as Row);

    // Resident Detail publishes one command per section — around twenty — and
    // an empty palette listing all of them buries the pages under a wall she
    // has to arrow past, which is the problem the directory existed to solve.
    // Unfiltered they are capped; typing finds every one of them.
    const actions = q ? matched : matched.slice(0, MAX_ACTIONS);

    if (!query.trim()) {
      return [
        // What she can DO here comes before where she can GO, because the
        // palette was opened from a screen she is already on.
        ...actions,
        ...directory
        .filter((item) => item.route)
        .map((item) => ({
          kind: 'page',
          item: {
            kind: 'page',
            id: `page:${item.route}`,
            title: item.label,
            route: item.route!,
            icon: item.icon.replace(/^bi-/, ''),
          },
        }) as Row),
      ];
    }

    const pages = searchPages(query, searchablePages).slice(0, MAX_PAGES);
    const reports = catalog
      ? searchReports(query, catalog, isSlugDisabled).slice(0, MAX_REPORTS)
      : [];

    return [
      ...actions,
      ...pages.map((item) => ({ kind: 'page', item }) as Row),
      ...reports.map((item) => ({ kind: 'report', item }) as Row),
      ...matchedResidents.map((patient) => ({ kind: 'resident', patient }) as Row),
      ...itemHits.map((hit) => ({ kind: 'itemRecord', hit }) as Row),
      ...recipeHits.map((hit) => ({ kind: 'recipe', hit }) as Row),
    ];
  }, [query, directory, searchablePages, catalog, matchedResidents, boundShortcuts, itemHits, recipeHits]);

  // Clamp selected index
  useEffect(() => {
    if (selectedIndex >= rows.length) {
      setSelectedIndex(Math.max(0, rows.length - 1));
    }
  }, [rows.length, selectedIndex]);

  const handleSelect = useCallback(
    (row: Row) => {
      onClose();
      if (row.kind === 'action') {
        // Closed first: several handlers focus something, and focusing behind
        // an open modal loses the focus to the dialog's trap.
        row.shortcut.handler();
        return;
      }
      const path = rowPath(row);
      // The page being left may have unsaved changes. A page that guards its
      // own ways out (Resident Detail: Discard / Save & Leave / Cancel) asks
      // here too; a page that only marks itself dirty gets a plain question.
      if (leaveGuardTakesOver(path)) return;
      if (pageHasUnsavedMarker()) {
        void confirm({
          title: 'Unsaved changes',
          message: 'This page has changes that are not saved. Leave without saving them?',
          confirmLabel: 'Leave',
          cancelLabel: 'Stay',
          variant: 'warning',
        }).then((ok) => { if (ok) navigate(path); });
        return;
      }
      navigate(path);
    },
    [navigate, onClose, confirm],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Alt+1-9 picks a numbered row directly. With the box empty this is a
      // menu you operate by key: no arrowing, and the number is announced with
      // each row so it can be heard rather than seen.
      //
      // It needs Alt. A bare digit used to do this, which meant the first key
      // of a room-number search ("114") jumped to row 1 instead of typing: the
      // box is always focused, so there is no "not typing" moment to borrow.
      // Alt+digit types nothing in a text box, and Ctrl+digit is left alone
      // because the browser uses it to switch tabs. e.code, not e.key: on a Mac
      // Option+1 reports e.key as "¡".
      const digit = /^Digit([1-9])$/.exec(e.code)?.[1]
        ?? (/^[1-9]$/.test(e.key) ? e.key : undefined);
      if (!query.trim() && e.altKey && !e.ctrlKey && !e.metaKey && digit) {
        const target = rows[Number(digit) - 1];
        if (target) {
          e.preventDefault();
          handleSelect(target);
          return;
        }
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, rows.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && rows[selectedIndex]) {
        e.preventDefault();
        handleSelect(rows[selectedIndex]);
      }
    },
    [rows, selectedIndex, handleSelect],
  );

  // This searches across ALL residents, so every string here is plural. It
  // used `residentLabel` — the SINGULAR — throughout, which produced "Search
  // Patient by name, room, or unit" and "search across all patient".
  const label = residentPluralLabel || 'Residents';
  // "residents, items, recipes": only what this user's role can open.
  const recordWords = [
    label.toLowerCase(),
    ...(canSearchItems ? ['items'] : []),
    ...(canSearchRecipes ? ['recipes'] : []),
  ];
  // Which capped lists hit their cap, for the "type more to narrow" hint.
  const capped = [
    ...(matchedResidents.length >= MAX_RESIDENTS ? [`${MAX_RESIDENTS} ${label.toLowerCase()}`] : []),
    ...(itemHits.length >= MAX_ITEMS ? [`${MAX_ITEMS} items`] : []),
    ...(recipeHits.length >= MAX_RECIPES ? [`${MAX_RECIPES} recipes`] : []),
  ];

  const sectionLabel = (kind: Row['kind']) =>
    kind === 'resident' ? label : SECTION_LABELS[kind];

  /**
   * Says how many matched, and what to do about it.
   *
   * The palette showed a list and announced nothing: typing "calorie" produced
   * three results in silence, so the only way to learn there were three was to
   * arrow to the bottom and hit the end. The footer does say "Enter to open",
   * but as visual <kbd> elements that focus never reaches — focus stays in the
   * input by design (this is the W3C combobox pattern), so she never gets
   * there.
   *
   * Debounced: typing is fast and this fires on every change, which would talk
   * over her mid-word. 400ms is long enough to land on a settled query.
   */
  const [resultMessage, setResultMessage] = useState('');
  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => {
      if (!query.trim()) {
        // The empty palette is the directory, and already announces itself as
        // one via the numbered rows.
        setResultMessage('');
        return;
      }
      if (rows.length === 0) {
        setResultMessage(`No results for ${query}`);
        return;
      }
      setResultMessage(
        `${rows.length} ${rows.length === 1 ? 'result' : 'results'}. `
        + 'Use up and down arrows to move through them, then Enter to open.',
      );
    }, 400);
    return () => clearTimeout(id);
  }, [open, query, rows.length]);

  const rowId = (index: number) => `global-search-option-${index}`;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        aria-labelledby="global-search-title"
        slotProps={{
          paper: { className: 'global-search-dialog' },
          backdrop: { sx: { background: 'rgba(0,0,0,0.35)' } },
        }}
      >
        {/* Screen-reader only: the count is visible to everyone else as the list
            itself. aria-atomic so the whole sentence is re-read rather than just
            the number that changed. */}
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {resultMessage}
        </div>

        <div className="global-search-input-wrap" onKeyDown={handleKeyDown}>
          <i className="bi bi-search global-search-icon" aria-hidden="true" />
          <input
            ref={inputRef}
            id="global-search-title"
            type="text"
            className="global-search-input"
            placeholder={`Search ${recordWords.join(', ')}, pages and reports...`}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            autoComplete="off"
            spellCheck={false}
            role="combobox"
            aria-expanded={rows.length > 0}
            aria-controls="global-search-listbox"
            aria-activedescendant={rows.length > 0 ? rowId(selectedIndex) : undefined}
          />
          <kbd className="global-search-kbd">Esc</kbd>
        </div>

        {rows.length > 0 && (
          <div
            className="global-search-results"
            role="listbox"
            id="global-search-listbox"
            aria-label="Search results"
          >
            {rows.length === 0 ? (
              <div className="global-search-empty">
                <i className="bi bi-search" aria-hidden="true" />
                Nothing found matching &ldquo;{query}&rdquo;
              </div>
            ) : (
              rows.map((row, i) => {
                // Section heading whenever the kind changes.
                const isFirstOfKind = i === 0 || rows[i - 1].kind !== row.kind;

                return (
                  <div key={rowKey(row)}>
                    {isFirstOfKind && (
                      <div className="global-search-section" aria-hidden="true">
                        {sectionLabel(row.kind)}
                      </div>
                    )}
                    {/* Focus stays in the input and the active row is announced
                        via aria-activedescendant — the W3C combobox pattern. The
                        rows are therefore mouse affordances only and need no key
                        handlers of their own; arrow keys are handled above.

                        jsx-a11y/click-events-have-key-events still flags this,
                        because the rule matches on the element rather than the
                        pattern. Left flagged rather than disabled: the count is
                        how Phase 1 is tracked, and a suppressed finding is
                        invisible to it. Revisit as a rule-level exception if the
                        same pattern shows up elsewhere. */}
                    <div
                      id={rowId(i)}
                      role="option"
                      aria-selected={i === selectedIndex}
                      className={`global-search-item${i === selectedIndex ? ' selected' : ''}`}
                      onClick={() => handleSelect(row)}
                      onMouseEnter={() => setSelectedIndex(i)}
                    >
                      {row.kind === 'action' ? (
                        <>
                          <div className="global-search-item-name">
                            <i className="bi bi-lightning-charge" aria-hidden="true" />{' '}
                            {!query.trim() && i < 9 && (
                              <span className="global-search-num">{`${i + 1} `}</span>
                            )}
                            {row.shortcut.label}
                          </div>
                          {/* The key is part of the row's text so it is spoken:
                              hearing "Add item, Ctrl plus N" teaches the
                              shortcut while she uses the slower way to reach it. */}
                          <div className="global-search-item-detail">
                            <span>{formatShortcutKey(row.shortcut)}</span>
                          </div>
                        </>
                      ) : row.kind === 'itemRecord' ? (
                        <>
                          <div className="global-search-item-name">
                            <i className="bi bi-basket" aria-hidden="true" /> {row.hit.itm_Descr}
                          </div>
                          {row.hit.menuDisplayName && row.hit.menuDisplayName !== row.hit.itm_Descr && (
                            <div className="global-search-item-detail">
                              <span>Menu: {row.hit.menuDisplayName}</span>
                            </div>
                          )}
                        </>
                      ) : row.kind === 'recipe' ? (
                        <>
                          <div className="global-search-item-name">
                            <i className="bi bi-journal-text" aria-hidden="true" /> {recipeDisplayName(row.hit)}
                          </div>
                          {(row.hit.recipeNumber || (row.hit.menuName && row.hit.menuName !== row.hit.name) || row.hit.isPsi) && (
                            <div className="global-search-item-detail">
                              {row.hit.recipeNumber && <span>No. {row.hit.recipeNumber}</span>}
                              {row.hit.menuName && row.hit.menuName !== row.hit.name && <span>Menu: {row.hit.menuName}</span>}
                              {row.hit.isPsi && <span>PSI recipe</span>}
                            </div>
                          )}
                        </>
                      ) : row.kind === 'resident' ? (
                        <>
                          <div className="global-search-item-name">
                            {row.patient.fullName ??
                              `${row.patient.lastName}, ${row.patient.firstName}`}
                          </div>
                          <div className="global-search-item-detail">
                            {row.patient.unit && (
                              <span><i className="bi bi-building" aria-hidden="true" /> {row.patient.unit}</span>
                            )}
                            {row.patient.room_No != null && (
                              <span>
                                <i className="bi bi-door-open" aria-hidden="true" /> {row.patient.room_No}
                                {row.patient.bed_No ? row.patient.bed_No : ''}
                              </span>
                            )}
                            {row.patient.dietOrder && (
                              <span><i className="bi bi-cup-straw" aria-hidden="true" /> {row.patient.dietOrder}</span>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="global-search-item-name">
                            <i className={`bi bi-${row.item.icon}`} aria-hidden="true" />{' '}
                            {/* The number is part of the option's text so a
                                screen reader says "1 Dashboard" — a shortcut you
                                can hear rather than one you have to see. */}
                            {!query.trim() && i < 9 && (
                              // The trailing space is inside the text on purpose:
                              // the CSS margin creates VISUAL separation only, and
                              // without a real space this announced as
                              // "onedashboard".
                              <span className="global-search-num">{`${i + 1} `}</span>
                            )}
                            {row.item.title}
                          </div>
                          {row.item.subtitle && (
                            <div className="global-search-item-detail">
                              <span>{row.item.subtitle}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            {capped.length > 0 && (
              <div className="global-search-hint">
                Showing the first {capped.join(', ')}. Type more to narrow.
              </div>
            )}
          </div>
        )}

        {searchingRecords && (
          <div className="global-search-hint global-search-hint--pending" aria-hidden="true">
            Searching {[canSearchItems ? 'items' : '', canSearchRecipes ? 'recipes' : ''].filter(Boolean).join(' and ')}...
          </div>
        )}

        {!query.trim() && (
          <div className="global-search-results">
            <div className="global-search-hint global-search-hint--empty">
              <i className="bi bi-lightbulb" aria-hidden="true" />
              Press Alt+1-9 to jump straight to a page, or type to search {recordWords.join(', ')},
              pages and reports
            </div>
          </div>
        )}

        {/* Arrow-key navigation and Enter-to-open were already wired up and
            entirely undiscoverable. A palette that can be driven from the
            keyboard should say so. */}
        <div className="global-search-footer">
          <span><kbd>&uarr;</kbd><kbd>&darr;</kbd> navigate</span>
          <span><kbd>&crarr;</kbd> open</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </Dialog>
      {/* Outside the palette: it has closed by the time this asks. */}
      <ConfirmDialog {...confirmProps} />
    </>
  );
}
